<script setup>
import { onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import AgentConsoleAPI from 'dashboard/api/agentConsole';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t, locale } = useI18n();
const route = useRoute();
const consoleUrl = ref('');
const configured = ref(null);
const loading = ref(true);
let baseUrl = '';

// 语言跟随宿主（kernel 页面按 ?locale= 渲染整套文案，含权限点业务名）。
const embedLocale = () => (String(locale.value) === 'en' ? 'en' : 'zh');

const buildConsoleUrl = () => {
  if (!baseUrl) return '';
  const tab = route.meta?.consoleTab || 'kb';
  const tabs = route.meta?.consoleTabs;
  const query = `tab=${encodeURIComponent(tab)}${tabs ? `&tabs=${encodeURIComponent(tabs)}` : ''}&locale=${embedLocale()}`;
  return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${query}`;
};

const loadConsoleUrl = async () => {
  loading.value = true;
  try {
    const { data } = await AgentConsoleAPI.get();
    configured.value = !!data?.configured;
    baseUrl = data?.url || '';
    consoleUrl.value = buildConsoleUrl();
    if (!data?.configured && data?.hint) {
      useAlert(data.hint);
    }
  } catch (error) {
    configured.value = false;
    useAlert(t('KNOWLEDGE_BASE.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  loadConsoleUrl();
});

watch(
  () => [route.meta?.consoleTab, route.meta?.consoleTabs, locale.value],
  () => {
    if (configured.value) {
      consoleUrl.value = buildConsoleUrl();
    }
  }
);

watch(
  () => route.params.accountId,
  (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      loadConsoleUrl();
    }
  }
);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden">
    <div v-if="loading" class="flex items-center justify-center flex-1">
      <Spinner class="text-n-slate-10" />
    </div>
    <div
      v-else-if="!configured"
      class="flex flex-col items-center justify-center flex-1 gap-2 p-8 text-center"
    >
      <span class="i-lucide-library size-10 text-n-slate-8" />
      <p class="font-medium text-n-slate-12">
        {{ $t('KNOWLEDGE_BASE.NOT_CONFIGURED_TITLE') }}
      </p>
      <p class="text-sm text-n-slate-11">
        {{ $t('KNOWLEDGE_BASE.NOT_CONFIGURED_HELP') }}
      </p>
    </div>
    <iframe
      v-else
      :src="consoleUrl"
      class="w-full flex-1 border-0 bg-transparent"
      :title="$t('KNOWLEDGE_BASE.TITLE')"
    />
  </div>
</template>
